import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Receipt,
  Boxes,
  CircleDollarSign,
  Loader2,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printSalesReport } from "@/lib/salesReportPrint";
import { displayItemName } from "@/lib/menuLocale";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import { ReportTrendPanel } from "@/components/reports/ReportTrendPanel";
import PaymentMethodFilter from "@/components/reports/PaymentMethodFilter";
import OrderTypeFilter from "@/components/reports/OrderTypeFilter";
import { formatReportFilterLine } from "@/lib/reportFilters";

const TABS = [
  { id: "daily", label: "Daily Sales", path: "/reports/daily-sales", icon: BarChart3 },
  { id: "counter", label: "Counter Sales", path: "/reports/counter-sales", icon: Receipt },
  { id: "items", label: "Item Wise", path: "/reports/item-sales", icon: Boxes },
  { id: "tax", label: "VAT Input / Output", path: "/reports/tax", icon: CircleDollarSign },
];

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

const ENDPOINTS = {
  daily: "/reports/sales/daily",
  counter: "/reports/sales/counter",
  items: "/reports/sales/items",
  tax: "/reports/sales/tax",
};

function SummaryCards({ summary, variant = "sales" }) {
  if (!summary) return null;

  const items =
    variant === "items"
      ? [
          { label: "Items", value: num(summary.item_count) },
          { label: "Total Qty", value: num(summary.total_qty) },
          { label: "Gross", value: aed(summary.gross_sales) },
          { label: "Discount", value: aed(summary.discount) },
          { label: "VAT", value: aed(summary.vat_collected) },
          { label: "Round off", value: aed(summary.round_off) },
          { label: "Net", value: aed(summary.net_sales), emphasize: true },
        ]
      : [
          { label: "Bills", value: num(summary.bill_count) },
          { label: "Gross", value: aed(summary.gross_sales) },
          { label: "Discount", value: aed(summary.discount) },
          { label: "Taxable", value: aed(summary.taxable_amount) },
          { label: "VAT", value: aed(summary.vat_collected) },
          { label: "Round off", value: aed(summary.round_off) },
          { label: "Net", value: aed(summary.net_sales), emphasize: true },
        ];

  return <ReportKpiStrip items={items} />;
}

export default function SalesReports() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();

  const activeTab = useMemo(() => {
    if (location.pathname.includes("counter-sales")) return "counter";
    if (location.pathname.includes("item-sales")) return "items";
    if (location.pathname.includes("/tax")) return "tax";
    return "daily";
  }, [location.pathname]);

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [counterId, setCounterId] = useState("all");
  const [orderType, setOrderType] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [highlightBill, setHighlightBill] = useState(null);

  useEffect(() => {
    const state = location.state;
    if (!state?.highlightBill) return;

    if (state.fromDate) setFromDate(state.fromDate);
    if (state.toDate) setToDate(state.toDate);
    setHighlightBill(state.highlightBill);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const showCounterFilter = activeTab !== "counter";

  const { data: countersData } = useQuery({
    queryKey: ["counters", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/counters", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId && showCounterFilter),
  });

  const counters = countersData?.counters ?? [];

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["sales-report", activeTab, branchId, allBranches, fromDate, toDate, counterId, orderType, paymentMethod],
    queryFn: async () => {
      const params = {
        ...reportParams,
        from_date: fromDate,
        to_date: toDate,
      };
      if (orderType !== "all") params.order_type = orderType;
      if (paymentMethod !== "all") params.payment_method = paymentMethod;
      if (showCounterFilter && counterId !== "all") params.counter_id = counterId;

      const { data: res } = await api.get(ENDPOINTS[activeTab], { params });
      return res;
    },
    enabled: Boolean(canQuery && fromDate && toDate),
  });

  const title = TABS.find((t) => t.id === activeTab)?.label ?? "Sales Reports";

  const chartData = useMemo(() => {
    if (activeTab === "tax") {
      const byDate = new Map();
      (report?.by_date || []).forEach((r) => {
        byDate.set(r.date, {
          date: r.date?.slice(5) || r.date,
          output: r.vat_collected ?? 0,
          input: 0,
        });
      });
      (report?.input?.by_date || []).forEach((r) => {
        const key = r.date;
        const existing = byDate.get(key) || { date: key?.slice(5) || key, output: 0, input: 0 };
        existing.input = r.vat_amount ?? 0;
        byDate.set(key, existing);
      });
      return [...byDate.values()];
    }
    if (activeTab === "daily") {
      return (report?.by_date || []).map((r) => ({
        date: r.date?.slice(5) || r.date,
        net: r.net_sales,
        vat: r.vat_collected ?? 0,
      }));
    }
    if (activeTab === "counter") {
      return (report?.rows || []).map((r) => ({
        name: r.counter_name,
        net: r.net_sales,
      }));
    }
    return (report?.rows || []).slice(0, 15).map((r) => {
      const label = displayItemName(r);
      return {
        name: label?.length > 18 ? `${label.slice(0, 18)}…` : label,
        net: r.net_sales,
      };
    });
  }, [activeTab, report, i18n.language]);

  return (
    <div className="min-w-0">
      <PageHeader
        title={title}
        subtitle={
          activeTab === "tax"
            ? "Output VAT on sales, input VAT on GRN purchases, and net VAT payable"
            : "Paid bills in the selected date range"
        }
        actions={
          <ReportExportButtons
            disabled={!report}
            title={title}
            filename={`sales-${activeTab}`}
            period={formatReportFilterLine({ fromDate, toDate, orderType, paymentMethod })}
            printTestId="sales-rpt-print"
            onPrint={() => printSalesReport(report, activeTab, { branchId, allBranches })}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
              data-testid={`sales-tab-${tab.id}`}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
            <Input
              type="date"
              className="h-9 w-[160px]"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="sales-from-date"
            />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
            <Input
              type="date"
              className="h-9 w-[160px]"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="sales-to-date"
            />
          </div>
          {showCounterFilter && (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Counter</Label>
              <Select value={counterId} onValueChange={setCounterId}>
                <SelectTrigger className="h-9 w-[180px]" data-testid="sales-counter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All counters</SelectItem>
                  {counters.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <OrderTypeFilter value={orderType} onChange={setOrderType} testId="sales-order-type" />
          <PaymentMethodFilter value={paymentMethod} onChange={setPaymentMethod} />
          {(isLoading || isFetching) && (
            <Pill className="mb-1">
              <Loader2 size={12} className="animate-spin mr-1" /> Loading…
            </Pill>
          )}
        </div>
      </Card>

      {report && (
        <>
          {activeTab !== "tax" && (
            <SummaryCards summary={report.summary} variant={activeTab === "items" ? "items" : "sales"} />
          )}

          {activeTab === "daily" && <DailyTables report={report} highlightBill={highlightBill} />}
          {activeTab === "counter" && <CounterTable rows={report.rows} />}
          {activeTab === "items" && <ItemTable rows={report.rows} />}
          {activeTab === "tax" && <TaxTables report={report} />}

          {chartData.length > 0 && (
            <ReportTrendPanel
              title={
                activeTab === "daily" || activeTab === "tax"
                  ? "Trend by date (optional)"
                  : "Top performers (optional)"
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey={activeTab === "daily" || activeTab === "tax" ? "date" : "name"}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis tick={{ fontSize: 11 }} width={56} />
                  <Tooltip formatter={(v) => aed(v)} />
                  {activeTab !== "tax" && (
                    <Bar dataKey="net" fill="#F36F21" name="Net sales" radius={[3, 3, 0, 0]} />
                  )}
                  {activeTab === "tax" && (
                    <>
                      <Bar dataKey="output" fill="#22A55B" name="Output VAT" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="input" fill="#3B82F6" name="Input VAT" radius={[3, 3, 0, 0]} />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </ReportTrendPanel>
          )}
        </>
      )}
    </div>
  );
}

function DailyTables({ report, highlightBill }) {
  return (
    <>
      <ReportTableCard title="By date">
        <table>
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-right">Bills</th>
              <th className="text-right">Gross</th>
              <th className="text-right">Discount</th>
              <th className="text-right">Taxable</th>
              <th className="text-right">VAT</th>
              <th className="text-right">Round off</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {(report.by_date || []).map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>
                <td className="text-right">{r.bill_count}</td>
                <td className="text-right font-mono">{aed(r.gross_sales)}</td>
                <td className="text-right font-mono">{aed(r.discount)}</td>
                <td className="text-right font-mono">{aed(r.taxable_amount)}</td>
                <td className="text-right font-mono">{aed(r.vat_collected)}</td>
                <td className="text-right font-mono">{aed(r.round_off)}</td>
                <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>

      {(report.by_order_type || []).length > 0 && (
        <ReportTableCard title="By order type">
          <table>
            <thead>
              <tr>
                <th className="text-left">Type</th>
                <th className="text-right">Bills</th>
                <th className="text-right">Round off</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {report.by_order_type.map((r) => (
                <tr key={r.order_type}>
                  <td>{r.order_type_label}</td>
                  <td className="text-right">{r.bill_count}</td>
                  <td className="text-right font-mono">{aed(r.round_off)}</td>
                  <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      )}

      <ReportTableCard title="Bills (latest 500)">
        <table>
          <thead>
            <tr>
              <th className="text-left">Bill</th>
              <th className="text-left">Paid</th>
              <th className="text-left">Type</th>
              <th className="text-left">Counter</th>
              <th className="text-left">Customer</th>
              <th className="text-left">TRN</th>
              <th className="text-right">Round off</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {(report.bills || []).map((b) => (
              <tr
                key={b.id}
                className={cn(
                  highlightBill && b.bill_number === highlightBill && "report-row-highlight",
                )}
              >
                <td className="font-mono text-[12px]">{b.bill_number}</td>
                <td>
                  {b.paid_date} {b.paid_time}
                </td>
                <td>{b.order_type_label}</td>
                <td>{b.counter_name || "—"}</td>
                <td>{b.customer_name || "—"}</td>
                <td className="font-mono text-[12px]">{b.customer_tax_number || "—"}</td>
                <td className="text-right font-mono">{aed(b.round_off)}</td>
                <td className="text-right font-mono font-medium">{aed(b.net_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function CounterTable({ rows }) {
  return (
    <ReportTableCard title="By counter">
      <table>
        <thead>
          <tr>
            <th className="text-left">Code</th>
            <th className="text-left">Counter</th>
            <th className="text-right">Bills</th>
            <th className="text-right">Gross</th>
            <th className="text-right">Discount</th>
            <th className="text-right">VAT</th>
            <th className="text-right">Round off</th>
            <th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.counter_id}>
              <td className="font-mono text-[12px]">{r.counter_code}</td>
              <td>{r.counter_name}</td>
              <td className="text-right">{r.bill_count}</td>
              <td className="text-right font-mono">{aed(r.gross_sales)}</td>
              <td className="text-right font-mono">{aed(r.discount)}</td>
              <td className="text-right font-mono">{aed(r.vat_collected)}</td>
              <td className="text-right font-mono">{aed(r.round_off)}</td>
              <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportTableCard>
  );
}

function ItemTable({ rows }) {
  return (
    <ReportTableCard title="Item wise sales">
      <table>
        <thead>
          <tr>
            <th className="text-left">Code</th>
            <th className="text-left">Item</th>
            <th className="text-right">Qty</th>
            <th className="text-right">Gross</th>
            <th className="text-right">Discount</th>
            <th className="text-right">VAT</th>
            <th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={`${r.menu_item_id}-${r.item_code}`}>
              <td className="font-mono text-[12px]">{r.item_code}</td>
              <td>{displayItemName(r)}</td>
              <td className="text-right">{num(r.qty)}</td>
              <td className="text-right font-mono">{aed(r.gross_sales)}</td>
              <td className="text-right font-mono">{aed(r.discount)}</td>
              <td className="text-right font-mono">{aed(r.vat_amount)}</td>
              <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportTableCard>
  );
}

function TaxTables({ report }) {
  const net = report.net || {};
  const input = report.input || {};
  const returns = report.output?.returns || report.returns || {};

  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Output VAT", value: aed(net.output_vat ?? report.summary?.vat_collected) },
          { label: "Return VAT", value: aed(net.return_vat ?? 0) },
          { label: "Input VAT", value: aed(net.input_vat ?? 0) },
          { label: "Net VAT payable", value: aed(net.net_vat_payable ?? 0), emphasize: true },
        ]}
      />

      <ReportTableCard title="Output VAT — sales by date">
        <table>
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-right">Bills</th>
              <th className="text-right">Taxable</th>
              <th className="text-right">Output VAT</th>
              <th className="text-right">Round off</th>
              <th className="text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {(report.by_date || []).map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>
                <td className="text-right">{r.bill_count}</td>
                <td className="text-right font-mono">{aed(r.taxable_amount)}</td>
                <td className="text-right font-mono">{aed(r.vat_collected)}</td>
                <td className="text-right font-mono">{aed(r.round_off)}</td>
                <td className="text-right font-mono font-medium">{aed(r.net_sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <ReportTableCard title="Output VAT — by order type" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Type</th>
                <th className="text-right">Taxable</th>
                <th className="text-right">Output VAT</th>
                <th className="text-right">Round off</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_order_type || []).map((r) => (
                <tr key={r.order_type}>
                  <td>{r.order_type_label}</td>
                  <td className="text-right font-mono">{aed(r.taxable_amount)}</td>
                  <td className="text-right font-mono font-medium">{aed(r.vat_collected)}</td>
                  <td className="text-right font-mono">{aed(r.round_off)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>

        <ReportTableCard title="Output VAT — by payment method" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Method</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_payment_method || []).map((r) => (
                <tr key={r.payment_method}>
                  <td>{r.payment_method}</td>
                  <td className="text-right font-mono font-medium">{aed(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      </div>

      {(returns.return_count || 0) > 0 && (
        <ReportTableCard title="Sales returns (reduce output VAT)">
          <table>
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-right">Returns</th>
                <th className="text-right">VAT</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {(returns.by_date || []).map((r) => (
                <tr key={r.date}>
                  <td>{r.date}</td>
                  <td className="text-right">{r.return_count}</td>
                  <td className="text-right font-mono">{aed(r.vat_amount)}</td>
                  <td className="text-right font-mono">{aed(r.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      )}

      <ReportTableCard title="Input VAT — purchases by date">
        <table>
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-right">GRNs</th>
              <th className="text-right">Taxable</th>
              <th className="text-right">Input VAT</th>
              <th className="text-right">Invoice total</th>
            </tr>
          </thead>
          <tbody>
            {(input.by_date || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="text-slate-500 text-[13px]">
                  No posted GRNs in this period. Set VAT % when posting a goods receipt.
                </td>
              </tr>
            ) : (
              (input.by_date || []).map((r) => (
                <tr key={r.date}>
                  <td>{r.date}</td>
                  <td className="text-right">{r.grn_count}</td>
                  <td className="text-right font-mono">{aed(r.taxable_amount)}</td>
                  <td className="text-right font-mono">{aed(r.vat_amount)}</td>
                  <td className="text-right font-mono font-medium">{aed(r.invoice_total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ReportTableCard>

      <ReportTableCard title="Input VAT — by supplier">
        <table>
          <thead>
            <tr>
              <th className="text-left">Supplier</th>
              <th className="text-right">GRNs</th>
              <th className="text-right">Taxable</th>
              <th className="text-right">Input VAT</th>
              <th className="text-right">Invoice total</th>
            </tr>
          </thead>
          <tbody>
            {(input.by_supplier || []).map((r) => (
              <tr key={r.supplier_id ?? r.supplier_name}>
                <td>{r.supplier_name}</td>
                <td className="text-right">{r.grn_count}</td>
                <td className="text-right font-mono">{aed(r.taxable_amount)}</td>
                <td className="text-right font-mono">{aed(r.vat_amount)}</td>
                <td className="text-right font-mono font-medium">{aed(r.invoice_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>

      <ReportTableCard title="Input VAT — GRN documents">
        <table>
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">GRN</th>
              <th className="text-left">Supplier</th>
              <th className="text-left">Invoice #</th>
              <th className="text-right">VAT %</th>
              <th className="text-right">Taxable</th>
              <th className="text-right">Input VAT</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {(input.rows || []).map((r) => (
              <tr key={r.id}>
                <td>{r.date}</td>
                <td className="font-mono text-[12px]">{r.receipt_no}</td>
                <td>{r.supplier_name || "—"}</td>
                <td>{r.supplier_invoice_no || "—"}</td>
                <td className="text-right">{r.vat_percent}%</td>
                <td className="text-right font-mono">{aed(r.taxable_amount)}</td>
                <td className="text-right font-mono">{aed(r.vat_amount)}</td>
                <td className="text-right font-mono font-medium">{aed(r.invoice_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}
